// useAppDevs — the app-wide FULL developments list (Conv #144: the List / Smart Grid, By Designer,
// the Board's client filters and fallback). Moved out of useAppData unchanged (AUD-05 — that file
// was at the 300-line cap); the one change is WHEN the first read starts, in devsAfterBoard.js.
import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "../supabase";
import { normalizeStatus, normalizeStageStatus } from "../shared";
import { DEV_LIST_SELECT } from "./devSelect";
import { DEADLINE_DEVS_KEY } from "./useDeadlineDevs";
import { TABS_READING_ALL_DEVS } from "./appShellLoads";
import { afterBoardFirstPage } from "./devsAfterBoard";

export function useAppDevs(queryClient) {
  const [devs, setDevs] = useState([]);
  const devsLoadedRef = useRef(false);   // true once the full read has STARTED (as before)
  const waitRef = useRef(null);          // cancel for a start still waiting on the Board

  // Shared devs fetch+flatten. Single source for loadDevsIfNeeded (guarded) and reloadDevs
  // (unguarded). Same DEV_LIST_SELECT projection + pagination + flatten as before.
  const fetchAndSetDevs = useCallback(async () => {
    let all = [], from = 0;
    while (true) {
      // D3 2026-09-14: created_at is shared by imports, so offset paging on it alone duplicated 5 rows and dropped 5 — id breaks the tie
      const { data, error } = await supabase.from("developments").select(DEV_LIST_SELECT).is("deleted_at", null).order("created_at", { ascending: false }).order("id").range(from, from + 999);
      if (error || !data || !data.length) break;
      all = [...all, ...data];
      if (data.length < 1000) break;
      from += 1000;
    }
    setDevs(all.map(d => {
      const flat = { ...d, ...(d.data || {}) };
      return { ...flat, full_status: normalizeStatus(flat.full_status), stage_status: normalizeStageStatus(flat.stage_status) };
    }));
  }, []);

  // Unguarded refresh — re-fetch devs regardless of devsLoadedRef. Wired to
  // DeadlinePanel postpone success so scopedDevsForDeadline (and the bell
  // badge) recompute after the RPC write. The panel reads useAppData.devs
  // (useState), which React Query invalidation cannot touch.
  const reloadDevs = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: DEADLINE_DEVS_KEY });        // the bell (useDeadlineDevs)
    if (devsLoadedRef.current) await fetchAndSetDevs();                     // the portal's full list, only once it has one
  }, [fetchAndSetDevs, queryClient]);

  const startDevs = useCallback(() => {
    waitRef.current = null;
    if (devsLoadedRef.current) return;
    devsLoadedRef.current = true;
    fetchAndSetDevs();
  }, [fetchAndSetDevs]);

  // Once per session, on the first entry into a screen that reads the full list (appShellLoads.js).
  const loadDevsFor = useCallback((tab) => {
    if (!TABS_READING_ALL_DEVS.has(tab) || devsLoadedRef.current) return;
    if (tab !== "developments") { if (waitRef.current) waitRef.current(); startDevs(); return; }
    if (waitRef.current) return;
    const cancel = afterBoardFirstPage(queryClient, startDevs);
    if (!devsLoadedRef.current) waitRef.current = cancel;
  }, [queryClient, startDevs]);

  useEffect(() => () => { if (waitRef.current) waitRef.current(); }, []);

  return { devs, setDevs, reloadDevs, loadDevsFor };
}
